<template>
	<h-radio-group-option
		:id="`configurator-attribute-option-${option.id}`"
		v-slot="{checked}"
		as="template"
		:value="value"
		:disabled="disabled"
	>
		<button
			v-if="type === 'default'"
			type="button"
			class="ring-focus group flex items-center space-x-xs py-xs text-sm leading-tight transition-opacity duration-75 outline-hidden"
			:class="{
				'opacity-25': disabled,
			}"
		>
			<span
				class="flex size-[18px] items-center justify-center rounded-full border-2 border-black transition-shadow duration-75 group-focus:ring"
			>
				<span
					class="size-[10px] rounded-full transition-colors duration-75"
					:class="{
						'bg-black': checked,
						'group-hover:bg-gray-200 group-focus:bg-gray-200 group-active:bg-gray-400':
							!checked,
					}"
				></span>
			</span>

			<span class="flex-1 text-left">
				{{ fullTitle }}
			</span>
		</button>

		<button
			v-else-if="type === 'size'"
			type="button"
			class="ring-focus my-xs flex items-center justify-center rounded-2xs border p-sm text-sm transition duration-75 outline-hidden"
			:class="{
				'opacity-25': disabled,
				'border-black ring ring-black/40': checked,
				'border-gray-100 hover:border-gray-200 active:border-gray-400':
					!checked,
			}"
		>
			<span class="truncate">
				{{ fullTitle }}
			</span>
		</button>

		<button
			v-else-if="type === 'color'"
			type="button"
			:disabled="disabled"
			class="ring-focus mt-xs mr-xs flex size-9 shrink-0 items-center justify-center rounded-full border bg-(--background-color) text-(--color) transition duration-75 outline-hidden"
			:class="{
				hidden: disabled,
				'border-black ring ring-black/40': checked,
				'border-gray-100 hover:border-gray-300 active:border-gray-400':
					!checked,
			}"
			:style="{
				'--color': iconColor,
				'--background-color': color || '#ffffff',
			}"
			:title="fullTitle"
		>
			<transition
				enter-active-class="transition-opacity duration-75 ease-out"
				enter-from-class="opacity-0"
				enter-to-class="opacity-100"
				leave-active-class="transition-opacity duration-75 ease-in"
				leave-from-class="opacity-100"
				leave-to-class="opacity-0"
			>
				<v-icon v-if="checked" icon="check" class="text-xl" />
			</transition>
		</button>

		<button
			v-else-if="type === 'image'"
			type="button"
			:disabled="disabled"
			class="ring-focus relative mt-xs mr-xs flex size-12 shrink-0 items-center justify-center overflow-hidden rounded border text-(--color) transition duration-75 outline-hidden"
			:class="{
				hidden: disabled,
				'border-black ring ring-black/40': checked,
				'border-gray-100 hover:border-gray-300 active:border-gray-400':
					!checked,
			}"
			:style="{
				'--color': iconColor,
			}"
			:title="fullTitle"
		>
			<nuxt-picture
				:src="image"
				:alt="fullTitle"
				class="absolute inset-0 size-full"
				loading="lazy"
				height="48"
				width="48"
				sizes="xs:48px"
				:img-attrs="{
					class: 'h-full w-full object-cover',
				}"
			/>

			<transition
				enter-active-class="transition-opacity duration-75 ease-out"
				enter-from-class="opacity-0"
				enter-to-class="opacity-100"
				leave-active-class="transition-opacity duration-75 ease-in"
				leave-from-class="opacity-100"
				leave-to-class="opacity-0"
			>
			</transition>
		</button>
	</h-radio-group-option>
</template>

<script lang="ts" setup>
import invertColor from "invert-color";
import type {ViewProductQuery} from "@pkg/shared/graphql";
import type {ProductKind} from "@pkg/shared/types";

type Configuration = Extract<
	ViewProductQuery["product"],
	{__typename: "ConfigurableProduct"}
>["configuration"];
type ConfigurableAttributeOption = Configuration["attributes"][0]["options"][0];

type ProductProperty = ProductKind<
	ViewProductQuery["product"]
>["properties"][0];
type ProductPropertyOption = ProductProperty["options"][0];

const properties = defineProps<{
	value: ProductPropertyOption | ConfigurableAttributeOption;
	option: ProductPropertyOption | ConfigurableAttributeOption["option"];
	prefix?: string;
	suffix?: string;
	type: "default" | "color" | "size" | "image";
	disabled?: boolean;
}>();

const iconColor = computed(() =>
	invertColor(properties.option.color || "#ffffff", true),
);

const image = computed(() => properties.option.image ?? undefined);
const color = computed(() => properties.option.color ?? undefined);

const fullTitle = computed(
	() =>
		(properties.prefix ?? "") +
		properties.option.title +
		(properties.suffix ?? ""),
);
</script>
